import React, { useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { SelectInput, StyledDiv } from 'up-ops-web-shared-components/lib';

import { FormIdentifier, RequestType } from 'lib/constants';
import { trackEvent } from 'lib/tracking';
import useSubmitActions from 'hooks/submit';
import useUploadActions from 'hooks/upload';
import { showSubmitSuccessState } from 'state/atoms/submit';
import IdentityTheftForm from 'views/forms/IdentityTheftForm';
import BankruptcyForm from 'views/forms/BankruptcyForm';
import CardTheftForm from 'views/forms/CardTheftForm';
import DeceasedForm from 'views/forms/DeceasedForm';
import IdentityVerificationForm from 'views/forms/IdentityVerificationForm';
import SuccessView from 'views/SuccessView';

const FormIdentifierMap = Object.fromEntries(
  Object.keys(RequestType).map((type) => [
    RequestType[type as keyof typeof RequestType],
    FormIdentifier[type as keyof typeof RequestType],
  ])
);

const getFormIdentifier = (requestType: string): string => {
  return FormIdentifierMap[requestType]?.toString();
};

const StyledInstructions = styled.div`
  border-bottom: 1px solid ${(props) => props.theme.colors.borderDefault};
  margin-bottom: 1.5rem;
  padding-bottom: 1.5rem;
`;

const FormsView = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const showSuccess = useRecoilValue(showSubmitSuccessState);
  const { reset: resetSubmit } = useSubmitActions();
  const { reset: resetUpload } = useUploadActions();

  const sortByRequestTypeName = useCallback(
    (a: string, b: string) => {
      const firstName = t(`forms.${a}.name`);
      const secondName = t(`forms.${b}.name`);
      return firstName.localeCompare(secondName);
    },
    [t]
  );

  let selected = 'none';
  if (location.pathname !== '/') {
    const pathSegments = location.pathname.split('/');
    selected = pathSegments[1];
  }

  return showSuccess ? (
    <SuccessView />
  ) : (
    <StyledDiv paddingBottom="l">
      <div>
        <Routes>
          <Route
            path="/"
            element={
              <StyledInstructions>
                {t('prompts.requestType')}
              </StyledInstructions>
            }
          />
          <Route path="*" element={null} />
        </Routes>
        <SelectInput
          label={t<string>('labels.requestType')}
          onChange={(data) => {
            resetSubmit();
            resetUpload();
            navigate(`/${data.value}`);
            trackEvent('user', 'click', 'change-form', {
              type: getFormIdentifier(data.value),
            });
          }}
          style={{ width: '50%' }}
          value={selected}
        >
          <option disabled value="none">
            {t('placeholders.requestType')}
          </option>
          {Object.values(RequestType)
            .sort(sortByRequestTypeName)
            .map((requestTypeValue) => (
              <option key={requestTypeValue} value={requestTypeValue}>
                {t(`forms.${requestTypeValue}.name`)}
              </option>
            ))}
        </SelectInput>
      </div>
      <Routes>
        <Route path="/bankruptcy" element={<BankruptcyForm />} />
        <Route path="/cardTheft" element={<CardTheftForm />} />
        <Route path="/deceased" element={<DeceasedForm />} />
        <Route path="/identityTheft" element={<IdentityTheftForm />} />
        <Route
          path="/identityVerification"
          element={<IdentityVerificationForm />}
        />
        <Route path="*" element={null} />
      </Routes>
    </StyledDiv>
  );
};

export default FormsView;
